import {
  Inject,
  LOCALE_ID,
  Component,
  OnInit,
  ViewChild,
  EventEmitter,
  Output,
  ChangeDetectorRef,
  Input,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroup, FormBuilder, FormControl, FormArray } from '@angular/forms';
import { Order } from '@models/order';
import { Patient } from '@models/patient';
import { OrderService } from '@services/order.service';
import { ProductService } from '@services/product.service';
import { StoreService } from '@services/store.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { environment } from '@environments/environment';
import { AuthService } from '@services/auth.service';
import { VendorOrderService } from '@services/vendor-order.service';
import {
  animate,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { BehaviorSubject } from 'rxjs';
import { OrdersBaseComponent } from '@components/admin/managed-board/base/orders-base/orders-base.component';
import { ImageDialogComponent } from '@components/admin/image/image-dialog/image-dialog.component';
import { ImageService } from '@services/image.service';
import { MatDialog } from '@angular/material/dialog';
import { ReturnOrderDialogComponent, ReturnOrderDialogResult } from '@components/admin/patient/return-order-dialog/return-order-dialog.component';
import { EmailOrderDialogComponent } from '@components/admin/patient/email-order-dialog/email-order-dialog.component';
import { DeleteOrderDialogComponent, DeleteOrderDialogResult } from '@components/admin/patient/delete-order-dialog/delete-order-dialog.component';
import { SaveComponent } from '@components/admin/dialog/save/save.component';
import { FormatData } from '@classes/format-data';
import { OrderDocument } from '@classes/order-document';
import { RepairJob } from '@classes/repair-job';
import { DuplicateJob } from '@classes/duplicate-job';
import { ExchangeJob } from '@classes/exchange-job';
import { NewRxJob } from '@classes/new-rx-job';
import { RedoJob } from '@classes/redo-job';
import { ReturnJob } from '@classes/return-job';
import { RedoOrderDialogComponent } from '@components/admin/patient/redo-order-dialog/redo-order-dialog.component';
import { RepairOrderDialogComponent } from '@components/admin/patient/repair-order-dialog/repair-order-dialog.component';
import { map, switchMap, takeWhile, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { NotificationService } from '@services/notification.service';
import { SettingsService } from '@services/settings.service';
import { PatientService } from '@services/patient.service';
import { OrderEditDialogComponent } from '@components/admin/patient/order-edit-dialog/order-edit-dialog.component';

@Component({
  selector: 'app-patient-orders',
  templateUrl: './patient-orders.component.html',
  styleUrls: ['./patient-orders.component.css'],
  animations: [
    trigger('detailExpand', [
      state(
        'collapsed',
        style({ height: '0px', minHeight: '0', visibility: 'hidden' })
      ),
      state('expanded', style({ height: '*', visibility: 'visible' })),
      transition(
        'expanded <=> collapsed',
        animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')
      ),
    ]),
  ],
})
export class PatientOrdersComponent
  extends OrdersBaseComponent
  implements OnInit
{
  @Input() public patient: Patient;
  @Input() public orderIndex: number;
  @Input() public storeCreditControl: FormControl;
  @Output() clickDelete = new EventEmitter<any>();
  @Output() clickReturn = new EventEmitter<any>();
  @Output() clickExchange = new EventEmitter<any>();
  @ViewChild(MatSort, { static: false }) sort: MatSort;
  orders: Order[];
  imageUrl: BehaviorSubject<any> = new BehaviorSubject('');

  displayedColumns = [
    'status',
    'vendor',
    'model',
    'color',
    'size',
    'type',
    'invoiceNumber',
    'view',
    'image',
    'action',
    'log',
  ];
  displayedColumns2 = [
    'notes',
    'part',
    'specialOrder',
    'warranty',
    'cost',
    'priceLink',
    'retail',
    'duty',
    'action',
  ];
  displayedColumns3 = [
    'notes',
    'part',
    'specialOrder',
    'warranty',
    'cost',
    'priceLink',
    'retail',
    'duty',
    'retailPlusDuty',
    'action',
  ];
  orderStatus = ['ORDER FRAME', 'ORDER ACCESSORY', 'ORDER PART'];
  awaitingStatus = ['AWAITING FRAME', 'AWAITING ACCESSORY', 'AWAITING PART', 'AWAITING FRAME + LENSES', 'AWAITING LENSES'];

  orderForm: FormGroup;

  fieldOpen: boolean = false;
  orderId: string;
  index: number = null;
  loaded: boolean = false;
  imageURL = environment.base_url + '/image/';

  constructor(
    @Inject(LOCALE_ID) public locale: string,
    public auth: AuthService,
    public orderService: OrderService,
    public vendorOrderService: VendorOrderService,
    public productService: ProductService,
    public storeService: StoreService,
    private imageService: ImageService,
    public cdRef: ChangeDetectorRef,
    public fb: FormBuilder,
    public dialog: MatDialog,
    public route: ActivatedRoute,
    public notificationService: NotificationService,
    private router: Router,
    public settingsService: SettingsService,
    private patientService: PatientService
  ) {
    super(
      locale,
      auth,
      orderService,
      vendorOrderService,
      productService,
      storeService,
      cdRef,
      fb,
      route,
      notificationService
    );
  }

  ngAfterViewInit() {
    this.getNewOrders();
  }

  ngOnInit() {
    super.ngOnInit();
  }

  getNewOrders() {
    const patientId = this.route.snapshot.params.id;
    if (!patientId) {
      this.loaded = true;
      return;
    }
    this.orderService
      .getPatientOrders(this.patient._id, 'orders')
      .pipe(
        map((orders) =>
          (orders || []).sort(
            (a, b) =>
              new Date(b.dateUpdated).getTime() -
              new Date(a.dateUpdated).getTime()
          )
        ),
        catchError(() => of([]))
      )
      .subscribe((res: Order[]) => {
        // Image-only / legacy scanned orders belong in Scanned Orders, not this table.
        this.orders = (res || []).filter(
          (order) => order.products && order.products.length > 0
        );
        this.setOrderForm(this.orders);
        for (let x = 0; x < this.orders.length; x++) {
          const orderFormGroup = this.orderForm.get('orders.' + x) as FormGroup;
          this.dataSource2.push(
            new MatTableDataSource<FormGroup>([orderFormGroup])
          );
        }
        this.loaded = true;
        this.cdRef.detectChanges();
      });
  }

  toggleDuty($event, order: FormGroup, index) {
    this.clickField2($event, order, index);
  }

  clickPart($event, order: FormGroup, index) {
    this.fieldOpen = true;
    this.orderId = order.value._id;
    this.index = index;
    if ($event.checked) {
      order.get('products.0.cost').setValue(0);
      order.get('products.0.retail').setValue(0);
      order.get('type').setValue('F');
    } else {
      order.get('type').setValue('O');
    }
  }

  clickField2($event, order: FormGroup, index) {
    this.fieldOpen = true;
    this.orderId = order.value._id;
    this.index = index;
  }

  addOrder($event, status?) {
    $event.preventDefault();
    $event.stopPropagation();
    this.router.navigate(['/admin/patient', this.patient._id, 'order', 'new']);
  }

  openOrderScreen(order: FormGroup): void {
    const orderId = order.value._id;
    if (!orderId) {
      return;
    }
    this.router.navigate(['/admin/patient', this.patient._id, 'order', orderId]);
  }

  startNewRx(orderFormGroup: FormGroup): void {
    const original = orderFormGroup.getRawValue();
    const originalInvoice = String(original?.invoiceNumber || '').trim();
    if (String(original?.status || '') !== 'DISPENSED') {
      this.notificationService.show(
        'error',
        'NEW RX is only available from a dispensed invoice',
        'OK'
      );
      return;
    }
    this.orderService
      .getPatientOrders(this.patient._id, 'orders')
      .pipe(
        switchMap((orders: Order[]) =>
          NewRxJob.create$(
            original,
            originalInvoice,
            orders,
            {
              patientId: this.patient._id,
              userId: this.auth.currentUser._id,
              store: this.auth.getStore(),
              initials:
                String(original?.initials || '').trim() ||
                String(this.auth.currentUser?.initials || '').trim(),
            },
            this.orderService
          )
        )
      )
      .subscribe({
        next: ({ created, taxCredit }) => {
          this.notificationService.show(
            'success',
            taxCredit
              ? 'New RX job created; previous Frame Only sales tax credited'
              : 'New RX job created; original invoice unchanged',
            'OK'
          );
          if (created?._id) {
            this.router.navigate([
              '/admin/patient',
              this.patient._id,
              'order',
              created._id,
            ]);
            return;
          }
          this.getNewOrders();
        },
        error: () => {
          this.notificationService.show(
            'error',
            'Could not create NEW RX job',
            'OK'
          );
        },
      });
  }

  startRedo(orderFormGroup: FormGroup): void {
    const original = orderFormGroup.getRawValue();
    const originalInvoice = String(original?.invoiceNumber || '').trim();
    if (String(original?.status || '') !== 'DISPENSED') {
      this.notificationService.show(
        'error',
        'RE-DO is only available from a dispensed invoice',
        'OK'
      );
      return;
    }
    const dialogRef = this.dialog.open(RedoOrderDialogComponent, {
      width: '360px',
      data: { invoiceNumber: originalInvoice },
    });
    dialogRef.afterClosed().subscribe((reason: string | null) => {
      if (!reason) {
        return;
      }
      this.orderService
        .getPatientOrders(this.patient._id, 'orders')
        .pipe(
          switchMap((orders: Order[]) =>
            RedoJob.create$(
              original,
              originalInvoice,
              orders,
              reason,
              {
                patientId: this.patient._id,
                userId: this.auth.currentUser._id,
                store: this.auth.getStore(),
                initials:
                  String(original?.initials || '').trim() ||
                  String(this.auth.currentUser?.initials || '').trim(),
              },
              this.orderService
            )
          )
        )
        .subscribe({
          next: (created: Order) => {
            this.notificationService.show(
              'success',
              'Re-do job created; original invoice unchanged',
              'OK'
            );
            if (created?._id) {
              this.router.navigate([
                '/admin/patient',
                this.patient._id,
                'order',
                created._id,
              ]);
              return;
            }
            this.getNewOrders();
          },
          error: () => {
            this.notificationService.show(
              'error',
              'Could not create RE-DO job',
              'OK'
            );
          },
        });
    });
  }

  startDuplicate(orderFormGroup: FormGroup): void {
    const original = orderFormGroup.getRawValue();
    const originalInvoice = String(original?.invoiceNumber || '').trim();
    const initials =
      String(original?.initials || '').trim() ||
      String(this.auth.currentUser?.initials || '').trim();
    const quantity = parseInt(original?.products?.[0]?.quantity, 10) || 1;

    DuplicateJob.lookupCurrentProduct(
      this.productService,
      original?.products?.[0]
    )
      .pipe(
        switchMap((currentProduct) => {
          const inStock = DuplicateJob.isInStock(currentProduct);
          return this.orderService.reserveInvoiceNumber().pipe(
            switchMap((invoiceNumber) => {
              const newOrder = DuplicateJob.buildDuplicateOrder(
                original,
                originalInvoice,
                invoiceNumber,
                currentProduct,
                inStock,
                {
                  patientId: this.patient._id,
                  userId: this.auth.currentUser._id,
                  store: this.auth.getStore(),
                  initials,
                }
              );
              return this.orderService.addOrder(newOrder).pipe(
                switchMap((createdOrders: Order[]) => {
                  const created = Array.isArray(createdOrders)
                    ? createdOrders[0]
                    : createdOrders;
                  if (!inStock || !currentProduct?._id) {
                    return of({ created, inStock });
                  }
                  return this.productService
                    .incrementProductInventoryQuantity(
                      currentProduct,
                      -Math.abs(quantity)
                    )
                    .pipe(
                      map(() => ({ created, inStock })),
                      catchError(() => {
                        this.notificationService.show(
                          'error',
                          'DUPLICATE CREATED; COULD NOT DEDUCT INVENTORY',
                          'OK'
                        );
                        return of({ created, inStock: false });
                      })
                    );
                })
              );
            })
          );
        })
      )
      .subscribe({
        next: ({ created, inStock }) => {
          this.notificationService.show(
            'success',
            inStock
              ? 'Duplicate job created with current pricing'
              : 'Duplicate job sent to TO ORDER with current pricing',
            'OK'
          );
          if (created?._id) {
            this.router.navigate([
              '/admin/patient',
              this.patient._id,
              'order',
              created._id,
            ]);
            return;
          }
          this.getNewOrders();
        },
        error: () => {
          this.notificationService.show(
            'error',
            'Could not create duplicate job',
            'OK'
          );
        },
      });
  }

  startRepair(orderFormGroup: FormGroup): void {
    const original = orderFormGroup.getRawValue();
    const originalInvoice = String(original?.invoiceNumber || '').trim();
    const dialogRef = this.dialog.open(RepairOrderDialogComponent, {
      width: '360px',
      data: { selected: original?.products?.[0]?.frame?.repairItems || [] },
    });
    dialogRef.afterClosed().subscribe((items: string[] | null) => {
      if (!items?.length) {
        return;
      }
      this.orderService
        .getPatientOrders(this.patient._id, 'orders')
        .pipe(
          switchMap((orders: Order[]) => {
            const invoiceNumber = RepairJob.nextFxInvoice(
              originalInvoice,
              orders
            );
            const newOrder = RepairJob.buildLinkedRepairOrder(
              original,
              originalInvoice,
              invoiceNumber,
              items,
              {
                patientId: this.patient._id,
                userId: this.auth.currentUser._id,
                store: this.auth.getStore(),
                initials:
                  String(original?.initials || '').trim() ||
                  String(this.auth.currentUser?.initials || '').trim(),
              }
            );
            return this.orderService.addOrder(newOrder);
          })
        )
        .subscribe((createdOrders: Order[]) => {
          const created = Array.isArray(createdOrders)
            ? createdOrders[0]
            : createdOrders;
          this.notificationService.show(
            'success',
            'Repair job sent to TO REPAIR',
            'OK'
          );
          if (created?._id) {
            this.router.navigate([
              '/admin/patient',
              this.patient._id,
              'order',
              created._id,
            ]);
            return;
          }
          this.getNewOrders();
        });
    });
  }

  openOrderEdit($event: Event, order: FormGroup): void {
    $event.preventDefault();
    $event.stopPropagation();
    const patientId = this.patient?._id || order.value?.patient?._id || order.value?.patient || null;
    const orderId = order.value?._id;
    if (!patientId || !orderId) {
      return;
    }

    const dialogRef = this.dialog.open(OrderEditDialogComponent, {
      width: '96vw',
      maxWidth: '96vw',
      height: '92vh',
      maxHeight: '92vh',
      autoFocus: false,
      panelClass: 'order-edit-dialog-panel',
      data: { patientId, orderId },
    });

    dialogRef.afterClosed().subscribe((saved) => {
      if (saved) {
        this.getNewOrders();
      }
    });
  }

  clickSaveOrder(order: FormGroup, destination, index) {
    order
      .get('todo')
      .setValue(destination == 'SEND TO STACK' ? 'ORDER' : destination);
    order
      .get('products.0.frame.frameSource')
      .setValue(
        destination == 'ORDERED ON APPROVAL' ? 'PATIENT APPROVAL' : 'PATIENT'
      );

    const newOrder = order.get('products.0._id').value == '' ? true : false;
    let status = this.getOrderStatusString(
      order.get('type').value,
      destination
    );

    if (destination == 'ADDED TO INVENTORY') {
      status = 'CANCELED';
      const invoiceNumber = order.get('invoiceNumber').value;
      order.get('invoiceNumber').setValue(invoiceNumber + '-C');

      this.productService
        .incrementProductInventoryQuantity(
          order.get('products.0.product').value,
          1
        )
        .subscribe((incrementProduct) => {
          if (!order.get('products.0.product._id').value) {
            order
              .get('products.0.product._id')
              .setValue(incrementProduct.product._id);

            this.vendorOrderService
              .addProductIdToVendorOrder(order.value)
              .subscribe((res) => {});
          }
          if (destination != 'SAVE') {
            order.get('status').setValue(status);
          }
          this.saveFrame(order, newOrder).subscribe((res) => {});
        });
    } else {
      if (destination != 'SAVE') {
        order.get('status').setValue(status);
      }
      this.saveFrame(order, newOrder).subscribe((res) => {});
    }
  }

  getOrderStatusString(type, destination): string {
    return /^ORDERED/.test(destination)
      ? this.getAwaitingStatusForType(type)
      : this.getStackOrderStatusForType(type);
  }

  getStackOrderStatusForType(type: string): string {
    switch (type) {
      case 'A':
        return 'ORDER ACCESSORY';
      case 'F':
        return 'ORDER PART';
      case 'O':
      case 'N':
      default:
        return 'ORDER FRAME';
    }
  }

  getAwaitingStatusForType(type: string): string {
    switch (type) {
      case 'A':
        return 'AWAITING ACCESSORY';
      case 'F':
        return 'AWAITING PART';
      case 'O':
      case 'N':
      default:
        return 'AWAITING FRAME';
    }
  }

  openImage(imageId) {
    this.imageService.getImageUrl(imageId).subscribe((res) => {
      this.imageUrl.next(res);
      this.openImageDialog(imageId, res);
    });
  }

  openImageDialog(imageId, imageUrl) {
    const dialogRef = this.dialog.open(ImageDialogComponent, {
      width: '',
      height: '',
      data: { image: imageUrl, imageId: imageId, message: '' },
    });
    dialogRef.afterClosed().subscribe((result) => {});
  }

  printImage(images) {
    OrderDocument.printImages(images, this.auth.getToken(), this.imageURL);
  }

  emailOrder(orderFormGroup: FormGroup): void {
    const order = orderFormGroup.getRawValue();
    const invoiceNumber = String(order?.invoiceNumber || '').trim();
    const dialogRef = this.dialog.open(EmailOrderDialogComponent, {
      width: '420px',
      data: {
        email: this.patient?.email || '',
        invoiceNumber,
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeWhile((email) => !!email))
      .subscribe((email: string) => {
        const logItem = OrderDocument.emailLogItem(
          String(this.auth.currentUser?.initials || '').trim(),
          this.auth.currentUser._id,
          email
        );
        this.orderService.updateOrderById(order._id, {
          log: [logItem],
          dateUpdated: new Date().toISOString(),
        }).subscribe({
          next: () => {
            OrderDocument.openMailto(
              email,
              invoiceNumber,
              this.auth.getStoreObject()?.storeName
            );
            this.notificationService.show(
              'success',
              'Invoice emailed to customer',
              'OK'
            );
          },
          error: () => {
            this.notificationService.show(
              'error',
              'Could not record email in the LOG',
              'OK'
            );
          },
        });
      });
  }

  deleteOrder(orderFormGroup: FormGroup, index): void {
    if (!this.isAdminUser) {
      this.notificationService.show(
        'error',
        'DELETE is limited to administrators',
        'OK'
      );
      return;
    }
    const order: Order = orderFormGroup.value;
    const orderId = order._id;
    const dialogRef = this.dialog.open(DeleteOrderDialogComponent, {
      width: '420px',
      data: { invoiceNumber: order.invoiceNumber },
    });
    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result?.reason))
      .subscribe((result: DeleteOrderDialogResult) => {
        this.orderService
          .deleteOrder(orderId, {
            reason: result.reason,
            notes: result.notes,
            initials: String(this.auth.currentUser?.initials || '').trim(),
          })
          .subscribe(() => {
            let orderFormArray: FormArray;
            orderFormArray = this.orderForm.controls.orders as FormArray;
            orderFormArray.removeAt(index);
            this.setDataSource(this.orderForm.controls.orders['controls']);
            this.notificationService.show('success', 'ORDER DELETED', 'OK');
          });
      });
  }

  returnOrder(orderFormGroup: FormGroup): void {
    const original = orderFormGroup.getRawValue();
    const originalInvoice = String(original?.invoiceNumber || '').trim();
    const dialogRef = this.dialog.open(ReturnOrderDialogComponent, {
      width: '420px',
      data: {
        message: 'RETURN FRAME?',
        order: original,
        mode: 'return',
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result))
      .subscribe((result: ReturnOrderDialogResult) => {
        const dialogResult =
          result?.netCredit !== undefined
            ? result
            : dialogRef.componentInstance.buildResult();
        this.orderService
          .getPatientOrders(this.patient._id, 'orders')
          .pipe(
            switchMap((orders: Order[]) =>
              ReturnJob.create$(
                original,
                originalInvoice,
                orders,
                {
                  credit: dialogResult.credit,
                  restockingFee: dialogResult.restockingFee,
                  netCredit: dialogResult.netCredit,
                  restockingFeeApplied:
                    dialogResult.applyRestockingFee !== false,
                },
                {
                  patientId: this.patient._id,
                  userId: this.auth.currentUser._id,
                  store: this.auth.getStore(),
                  initials:
                    String(original?.initials || '').trim() ||
                    String(this.auth.currentUser?.initials || '').trim(),
                },
                {
                  orderService: this.orderService,
                  productService: this.productService,
                  patientService: this.patientService,
                }
              )
            )
          )
          .subscribe({
            next: (created: Order) => {
              const storeCreditValue =
                FormatData.formatFloat(this.storeCreditControl?.value) +
                FormatData.formatFloat(dialogResult.netCredit);
              this.storeCreditControl?.setValue(storeCreditValue);
              this.notificationService.show(
                'success',
                'Return processed; original invoice unchanged',
                'OK'
              );
              if (created?._id) {
                this.router.navigate([
                  '/admin/patient',
                  this.patient._id,
                  'order',
                  created._id,
                ]);
                return;
              }
              this.getNewOrders();
            },
            error: () => {
              this.notificationService.show(
                'error',
                'Could not create return',
                'OK'
              );
            },
          });
      });
  }

  exchangeOrder(orderFormGroup: FormGroup): void {
    const original = orderFormGroup.getRawValue();
    const originalInvoice = String(original?.invoiceNumber || '').trim();
    const dialogRef = this.dialog.open(ReturnOrderDialogComponent, {
      width: '420px',
      data: {
        message: 'EXCHANGE FRAME?',
        order: original,
        mode: 'exchange',
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeWhile((result) => !!result))
      .subscribe((result: ReturnOrderDialogResult) => {
        const dialogResult =
          result?.netCredit !== undefined
            ? result
            : dialogRef.componentInstance.buildResult();
        this.orderService
          .getPatientOrders(this.patient._id, 'orders')
          .pipe(
            switchMap((orders: Order[]) =>
              ExchangeJob.create$(
                original,
                originalInvoice,
                orders,
                {
                  credit: dialogResult.credit,
                  restockingFee: dialogResult.restockingFee,
                  netCredit: dialogResult.netCredit,
                },
                {
                  patientId: this.patient._id,
                  userId: this.auth.currentUser._id,
                  store: this.auth.getStore(),
                  initials:
                    String(original?.initials || '').trim() ||
                    String(this.auth.currentUser?.initials || '').trim(),
                },
                {
                  orderService: this.orderService,
                  productService: this.productService,
                  patientService: this.patientService,
                }
              )
            )
          )
          .subscribe({
            next: (created: Order) => {
              const storeCreditValue =
                FormatData.formatFloat(this.storeCreditControl?.value) +
                FormatData.formatFloat(dialogResult.netCredit);
              this.storeCreditControl?.setValue(storeCreditValue);
              this.notificationService.show(
                'success',
                'Exchange job created; original invoice unchanged',
                'OK'
              );
              if (created?._id) {
                this.router.navigate([
                  '/admin/patient',
                  this.patient._id,
                  'order',
                  created._id,
                ]);
                return;
              }
              this.getNewOrders();
            },
            error: () => {
              this.notificationService.show(
                'error',
                'Could not create exchange job',
                'OK'
              );
            },
          });
      });
  }

  openDialog(message): void {
    const dialogRef = this.dialog.open(SaveComponent, {
      width: '250px',
      data: { message: message },
    });
    dialogRef.afterClosed().subscribe((result) => {
      console.log('The dialog was closed');
    });
  }
}
